import {
  Combination,
  CraftegoryGameState,
  Term,
  Category,
  ThemeDefinition,
} from '../Data/types';

/**
 * Finds a combination for two term ids (order-independent).
 * Returns null if no combination exists for this pair.
 */
export function findCombination(
  combinations: Combination[],
  t1: string,
  t2: string
): Combination | null {
  // Guard: a term cannot combine with itself
  if (t1 === t2) return null;

  return (
    combinations.find(
      (c) =>
        (c.term1Id === t1 && c.term2Id === t2) ||
        (c.term1Id === t2 && c.term2Id === t1)
    ) ?? null
  );
}

/**
 * Initializes runtime game state from a theme definition and previously saved data.
 * Seeds are always unlocked + mastered. Non-seeds start unlocked-but-unmastered
 * unless their termId appears in savedQuizResults.
 */
export function initializeGameState(
  theme: ThemeDefinition,
  savedUnlockedTermIds: string[],
  savedQuizResults: Record<string, number>
): CraftegoryGameState {
  const unlockedSet = new Set([...theme.seedTermIds, ...savedUnlockedTermIds]);
  const seedSet = new Set(theme.seedTermIds);

  const terms = new Map<string, Term>();
  const categories = new Map<string, Category>();

  for (const def of theme.terms) {
    const isUnlocked = unlockedSet.has(def.termId);
    // Seeds are always mastered; non-seeds are mastered only if they have a quiz result
    const isMastered = isUnlocked && (seedSet.has(def.termId) || def.termId in savedQuizResults);
    const term: Term = { ...def, isUnlocked, isMastered };
    terms.set(def.termId, term);

    if (!categories.has(def.categoryName)) {
      categories.set(def.categoryName, {
        name: def.categoryName,
        terms: [],
      });
    }
    categories.get(def.categoryName)!.terms.push(def.termId);
  }

  return {
    theme: theme.id,
    terms,
    categories,
    categoryOrder: theme.categoryOrder,
    possibleCombinations: theme.combinations,
    lastCombination: null,
  };
}

/**
 * Attempts to combine two terms in the game state.
 * Returns a new game state if successful, or null if the combination is invalid.
 *
 * A combination is invalid when:
 * - Either term is not mastered (unmastered terms cannot be used in combinations)
 * - The two terms are the same
 * - No combination exists for this pair
 * - The product term is already unlocked
 */
export function combineTerm(
  state: CraftegoryGameState,
  t1Id: string,
  t2Id: string
): CraftegoryGameState | null {
  if (t1Id === t2Id) return null;

  const t1 = state.terms.get(t1Id);
  const t2 = state.terms.get(t2Id);
  if (!t1?.isMastered || !t2?.isMastered) return null;

  const combo = findCombination(state.possibleCombinations, t1Id, t2Id);
  if (!combo) return null;

  // Ensure the product is not one of the inputs (data integrity guard)
  if (combo.productTermId === t1Id || combo.productTermId === t2Id) return null;

  const product = state.terms.get(combo.productTermId);
  if (!product || product.isUnlocked) return null;

  const newTerms = new Map(state.terms);
  newTerms.set(combo.productTermId, { ...product, isUnlocked: true });

  return {
    ...state,
    terms: newTerms,
    lastCombination: combo,
  };
}

/**
 * Marks a term as mastered in the game state.
 * Returns a new game state with the term's isMastered flag set to true.
 */
export function masterTerm(
  state: CraftegoryGameState,
  termId: string
): CraftegoryGameState {
  const term = state.terms.get(termId);
  if (!term || term.isMastered) return state;

  const newTerms = new Map(state.terms);
  newTerms.set(termId, { ...term, isMastered: true });

  return { ...state, terms: newTerms };
}

/**
 * Returns true when every term in the game state is mastered.
 */
export function isGameWon(state: CraftegoryGameState): boolean {
  for (const term of state.terms.values()) {
    if (!term.isMastered) return false;
  }
  return true;
}

/**
 * Returns the termIds of all currently unlocked non-seed terms.
 */
export function getUnlockedNonSeedTermIds(
  state: CraftegoryGameState,
  seedTermIds: string[]
): string[] {
  const seedSet = new Set(seedTermIds);
  const result: string[] = [];
  for (const [termId, term] of state.terms) {
    if (term.isUnlocked && !seedSet.has(termId)) {
      result.push(termId);
    }
  }
  return result;
}

/**
 * Returns the fraction of categories that are fully complete (all terms unlocked).
 */
export function getCategoryProgress(state: CraftegoryGameState): {
  completed: number;
  total: number;
} {
  let completed = 0;
  for (const category of state.categories.values()) {
    const allUnlocked = category.terms.every(
      (name) => state.terms.get(name)?.isUnlocked
    );
    if (allUnlocked) completed++;
  }
  return { completed, total: state.categories.size };
}
